<template>
  <DashboardLayout>
    <div class="d-flex align-items-center gap-2 mb-4 page-identifier">
      <p class="mb-0 main-identifier fw-semibold">Reports</p>
      <span class="material-symbols-outlined">keyboard_arrow_right</span>
      <p class="mb-0 sub-identifier">Daily Summary</p>
    </div>

    <div class="d-flex justify-content-center">
      <div class="form-outer">
        <div class="view-item position-relative">
          <div class="form-inner">
            <div class="form-header d-flex justify-content-between align-items-center report">
              <h1 class="mb-0">Daily Summary Report</h1>
            </div>

            <div class="form-body">
              <div class="mb-3">
                <div class="month-display">
                  <h3 class="mb-3">{{ monthHeader }}</h3>
                </div>

                <div class="dropdowns-wrapper">
                  <div class="dropdown-container">
                    <label class="sr-only" for="monthSelect">Select month</label>
                    <div class="search-box">
                      <span class="material-symbols-outlined search-icon">calendar_month</span>
                      <select
                        id="monthSelect"
                        v-model="selectedMonthNumber"
                        class="form-control form-control-sm dropdown-input"
                        aria-label="Select month"
                      >
                        <option v-for="m in months" :key="m.value" :value="m.value">
                          {{ m.label }}
                        </option>
                      </select>
                    </div>
                  </div>

                  <div class="dropdown-container">
                    <label class="sr-only" for="yearSelect">Select year</label>
                    <div class="search-box">
                      <span class="material-symbols-outlined search-icon">calendar_today</span>
                      <select
                        id="yearSelect"
                        v-model="selectedYear"
                        class="form-control form-control-sm dropdown-input"
                        aria-label="Select year"
                      >
                        <option v-for="y in years" :key="y" :value="y">
                          {{ y }}
                        </option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div class="table-container">
                <div v-if="loadingSummary" class="loading-state text-center py-5">
                  <div class="spinner-border text-primary" role="status" aria-hidden="true"></div>
                  <p class="mt-2 mb-0 text-muted">Loading daily summary...</p>
                </div>

                <table v-else class="summary-table">
                  <thead>
                    <tr>
                      <th>DATE RECEIVED</th>
                      <th>NUMBER OF FILES</th>
                      <th>COMPLETED</th>
                      <th>PENDING</th>
                      <th>RETURNED</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="rows.length === 0">
                      <td colspan="5" class="text-center">No records for this month</td>
                    </tr>

                    <tr
                      v-for="(row, idx) in rows"
                      :key="idx"
                      :class="idx % 2 === 0 ? 'row-even' : 'row-odd'"
                      v-else
                    >
                      <td data-label="DATE RECEIVED">{{ formatShortDate(row.date) }}</td>
                      <td data-label="NUMBER OF FILES" class="text-center">
                        <span class="clickable-number" @click="openModal(row.date, '0')">
                          {{ row.count }}
                        </span>
                      </td>
                      <td data-label="COMPLETED" class="text-center">
                        <span class="clickable-number" @click="openModal(row.date, '1')">
                          {{ row.completed }}
                        </span>
                      </td>
                      <td data-label="PENDING" class="text-center">
                        <span class="clickable-number" @click="openModal(row.date, '2')">
                          {{ row.pending }}
                        </span>
                      </td>
                      <td data-label="RETURNED" class="text-center">
                        <span class="clickable-number" @click="openModal(row.date, '3')">
                          {{ row.returned }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr class="totals-row">
                      <td data-label="" class="fw-bold">TOTAL</td>
                      <td data-label="NUMBER OF FILES" class="text-center fw-bold">{{ totals.count }}</td>
                      <td data-label="COMPLETED" class="text-center fw-bold">{{ totals.completed }}</td>
                      <td data-label="PENDING" class="text-center fw-bold">{{ totals.pending }}</td>
                      <td data-label="RETURNED" class="text-center fw-bold">{{ totals.returned }}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Popup -->
    <div v-if="showModal" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3 class="mb-0">{{ modalTitle }}</h3>
          <button class="close-btn" @click="closeModal" aria-label="Close modal">
            <span class="material-symbols-outlined">close</span>
          </button>
        </div>
        
        <div class="modal-body">
          <div v-if="loadingFiles" class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2">Loading files...</p>
          </div>
          
          <div v-else-if="fileList.length === 0" class="text-center py-4">
            <span class="material-symbols-outlined empty-icon">folder_open</span>
            <p class="mt-2">No files found for this date and status.</p>
          </div>
          
          <div v-else class="file-list-container">
            <div v-for="(file, idx) in fileList" :key="idx" class="file-item">
              <div class="file-icon-wrapper">
                <span class="material-symbols-outlined file-icon">
                  {{ file.fileType === 'Memo' ? 'description' : 'rate_review' }}
                </span>
              </div>
              <div class="file-info">
                <div class="file-header">
                  <span class="file-type-badge" :class="file.fileType === 'Memo' ? 'badge-memo' : 'badge-appraisal'">
                    {{ file.fileType }}
                  </span>
                  <span class="file-status-badge" :class="getStatusClass(file.status)">
                    {{ file.status }}
                  </span>
                </div>
                <div class="file-details">
                  <p class="file-number mb-1">
                    <strong>File Number:</strong> {{ file.fileNumber }}
                  </p>
                  <p class="file-mda mb-1">
                    <strong>MDA:</strong> {{ file.mda }}
                  </p>
                  <p class="file-amount mb-2">
                    <strong>Amount:</strong> ₦{{ formatAmount(file.amount) }}
                  </p>
                </div>
                <router-link 
                  :to="file.fileType === 'Memo' 
                      ? { name: 'ViewMemo', params: { id: file.fileId } }
                      : { name: 'ViewAppraisal', params: { id: file.fileId } 
                  }"
                  class="btn btn-sm btn-view"
                  @click="closeModal">
                  <span class="material-symbols-outlined">visibility</span>
                  View Details
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </DashboardLayout>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import axios from 'axios'
import Swal from 'sweetalert2'
import DashboardLayout from '../layout/DashboardLayout.vue'

const root_api_url = process.env.VUE_APP_API_ROOT_URL || ''
const now = new Date()
const pad = (n) => String(n).padStart(2, '0')

// Initialize with current month and year
const selectedMonthNumber = ref(now.getMonth() + 1)
const selectedYear = ref(now.getFullYear())
const rows = ref([])
const loadingSummary = ref(true)

// Modal state
const showModal = ref(false)
const modalTitle = ref('')
const fileList = ref([])
const loadingFiles = ref(false)

// Generate months array
const months = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' }
]

// Generate years array (current year and 5 years back)
const years = computed(() => {
  const currentYear = now.getFullYear()
  const yearsList = []
  for (let i = 0; i <= 5; i++) {
    yearsList.push(currentYear - i)
  }
  return yearsList
})

const monthHeader = computed(() => {
  const monthName = months.find(m => m.value === selectedMonthNumber.value)?.label.toUpperCase() || ''
  return `${monthName} ${selectedYear.value} DAILY SUMMARY`
})

// Calculate totals
const totals = computed(() => {
  return rows.value.reduce((acc, row) => ({
    count: acc.count + row.count,
    completed: acc.completed + row.completed,
    pending: acc.pending + row.pending,
    returned: acc.returned + row.returned
  }), { count: 0, completed: 0, pending: 0, returned: 0 })
})

const formatShortDate = (date) => {
  if (!(date instanceof Date)) date = new Date(date)
  const mm = pad(date.getMonth() + 1)
  const dd = pad(date.getDate())
  const yyyy = date.getFullYear()
  return `${dd}/${mm}/${yyyy}`
}

const formatAmount = (amount) => {
  return new Intl.NumberFormat('en-NG', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount)
}

const getStatusClass = (status) => {
  const statusLower = status?.toLowerCase() || ''
  if (statusLower === 'completed') return 'badge-completed'
  if (statusLower === 'pending') return 'badge-pending'
  if (statusLower === 'returned') return 'badge-returned'
  return ''
}

async function fetchMonthSummary(month, year) {
  const url = `${root_api_url}/appraisal/fileSummary/${month}/${year}`

  try {
    const resp = await axios.get(url)
    const data = resp.data ?? []
    const mapped = data.map(item => ({
      date: item.dateReceived ? new Date(item.dateReceived) : null,
      count: item.numberOfFiles ?? 0,
      completed: item.completedInd ?? 0,
      pending: item.pendingInd ?? 0,
      returned: item.returnedInd ?? 0
    }))
    const withDate = mapped.filter(r => r.date instanceof Date && !isNaN(r.date))
    withDate.sort((a, b) => a.date - b.date)
    return withDate
  } catch (err) {
    console.error('API fetch failed', err)
    return []
  }
}

// Function to fetch file list for a specific date and status
async function fetchFileList(date, status) {
  const formattedDate = formatShortDate(date)
  const url = `${root_api_url}/appraisal/fileSummary/detailedFileSum/${formattedDate}/${status}`

  try {
    const resp = await axios.get(url)
    const data = resp.data ?? []
    
    // Map the response based on the sample data structure
    return data.map(item => ({
      fileId: item.fileId,
      fileNumber: item.fileNumber,
      fileType: item.fileType,
      mda: item.mda,
      amount: item.amount,
      status: item.status,
      dateReceived: item.dateReceived
    }))
  } catch (err) {
    console.error('Failed to fetch file list', err)
    return []
  }
}

// Open modal and fetch file list
async function openModal(date, status) {
  showModal.value = true
  loadingFiles.value = true
  fileList.value = []
  
  const statusLabels = {
    '0': 'All Files',
    '1': 'Completed Files',
    '2': 'Pending Files',
    '3': 'Returned Files'
  }
  
  modalTitle.value = `${statusLabels[status]} - ${formatShortDate(date)}`
  
  try {
    fileList.value = await fetchFileList(date, status)
  } catch (err) {
    console.error('Error loading files', err)
  } finally {
    loadingFiles.value = false
  }
}

function closeModal() {
  showModal.value = false
  fileList.value = []
}

let ongoingFetchToken = 0
async function loadData() {
  loadingSummary.value = true
  rows.value = []
  const localToken = ++ongoingFetchToken
  try {
    const fetchedRows = await fetchMonthSummary(selectedMonthNumber.value, selectedYear.value)
    if (localToken !== ongoingFetchToken) return
    rows.value = fetchedRows
  } catch (err) {
    console.error('Load data error', err)
    rows.value = []
    const iconHtmlError = `<span class="iconify" data-icon="material-symbols:cancel-outline-rounded" style="font-size: 100px; color: red;"></span>`
    Swal.fire({
      html: `${iconHtmlError}
      <h1 style="font-weight:600;">Error</h1>
      <p>${err?.response?.data?.errorMessage || err?.response?.data?.error || err?.response?.data?.message || 'Failed to load daily summary. Please try again.'}</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    });
  } finally {
    if (localToken === ongoingFetchToken) {
      loadingSummary.value = false
    }
  }
}

watch([selectedMonthNumber, selectedYear], loadData)

onMounted(async () => {
  await loadData()
  document.title = 'Daily Summary Report'
})
</script>

<style scoped>
/* Header Controls - Responsive Layout */
.month-display {
  margin-bottom: 16px;
}

.month-display h3 {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color);
  margin: 0;
}

.dropdowns-wrapper {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.dropdown-container {
  flex: 0 0 auto;
  width: 180px;
}

.search-box {
  position: relative;
  display: inline-flex;
  align-items: center;
  width: 100%;
}

.search-icon {
  position: absolute;
  left: 10px;
  font-size: 1.1rem;
  opacity: 0.8;
  pointer-events: none;
  z-index: 1;
}

.dropdown-input {
  padding-left: 38px;
  padding-right: 12px;
  height: 36px;
  border-radius: 4px;
  border: 1px solid #d0d7de;
  background: #fff;
  font-size: 0.9rem;
  width: 100%;
  cursor: pointer;
}

.dropdown-input:focus {
  outline: none;
  border-color: #0969da;
  box-shadow: 0 0 0 3px rgba(9, 105, 218, 0.1);
}

/* Table Styles */
.table-container {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.summary-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.95rem;
}

.summary-table thead th {
  text-align: left;
  padding: 10px 12px;
  border-bottom: 2px solid #e6e6e6;
  background: #fafafa;
  font-weight: 600;
  white-space: nowrap;
}

.summary-table tbody td {
  padding: 10px 12px;
  border-bottom: 1px solid #f0f0f0;
}

.summary-table tfoot .totals-row {
  background: #f5f5f5;
  border-top: 2px solid #d0d7de;
}

.summary-table tfoot td {
  padding: 12px;
  font-size: 1rem;
}

.row-even {
  background: #ffffff;
}

.row-odd {
  background: #fbfbfb;
}

/* Clickable numbers styling */
.clickable-number {
  color: #0969da;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s;
  font-weight: 500;
  display: inline-block;
}

.clickable-number:hover {
  background: #ddf4ff;
  text-decoration: underline;
}

/* Modal styling */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease-in-out;
  padding: 16px;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.modal-content {
  background: white;
  border-radius: 8px;
  width: 100%;
  max-width: 700px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e6e6e6;
  background: #f8f9fa;
}

.modal-header h3 {
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: #666;
  transition: all 0.2s;
  border-radius: 4px;
  flex-shrink: 0;
}

.close-btn:hover {
  color: #000;
  background: #e9ecef;
}

.modal-body {
  padding: 16px 20px;
  overflow-y: auto;
}

.empty-icon {
  font-size: 4rem;
  color: #ccc;
}

/* File list styling */
.file-list-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.file-item {
  display: flex;
  align-items: flex-start;
  padding: 16px;
  border: 1px solid #e6e6e6;
  border-radius: 8px;
  background: #f8f9fa;
  transition: all 0.2s;
}

.file-item:hover {
  background: #fff;
  border-color: #0969da;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.file-icon-wrapper {
  flex-shrink: 0;
  margin-right: 16px;
  padding: 12px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.file-icon {
  color: #0969da;
  font-size: 2rem;
}

.file-info {
  flex: 1;
  min-width: 0;
}

.file-header {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.file-type-badge, .file-status-badge {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.badge-memo {
  background: #e3f2fd;
  color: #1976d2;
}

.badge-appraisal {
  background: #f3e5f5;
  color: #7b1fa2;
}

.badge-completed {
  background: #e8f5e9;
  color: #2e7d32;
}

.badge-pending {
  background: #fff3e0;
  color: #ef6c00;
}

.badge-returned {
  background: #ffebee;
  color: #c62828;
}

.file-details {
  margin-bottom: 12px;
}

.file-details p {
  font-size: 0.9rem;
  color: #555;
  line-height: 1.5;
  word-break: break-word;
}

.file-details strong {
  color: #333;
  font-weight: 600;
}

.btn-view {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background-color: #28a745;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.2s;
}

.btn-view:hover {
  background-color: #218838;
  color: white;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(40, 167, 69, 0.3);
}

.btn-view .material-symbols-outlined {
  font-size: 1.1rem;
}

.sr-only {
  position: absolute !important;
  height: 1px; 
  width: 1px;
  overflow: hidden;
  clip: rect(1px, 1px, 1px, 1px);
  white-space: nowrap;
}

/* Loading spinner */
.spinner-border {
  width: 3rem;
  height: 3rem;
  border-width: 0.3rem;
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {
  .month-display {
    text-align: center;
    margin-bottom: 12px;
  }

  .month-display h3 {
    font-size: 1rem;
  }

  .dropdowns-wrapper {
    flex-direction: column;
    width: 100%;
  }

  .dropdown-container {
    width: 100%;
  }

  /* Convert table to card layout on mobile */
  .summary-table {
    font-size: 0.85rem;
  }

  .summary-table thead {
    display: none;
  }

  .summary-table tbody,
  .summary-table tfoot {
    display: block;
  }

  .summary-table tbody tr,
  .summary-table tfoot tr {
    display: block;
    margin-bottom: 12px;
    border: 1px solid #e6e6e6;
    border-radius: 8px;
    padding: 12px;
    background: white;
  }

  .summary-table tbody tr td,
  .summary-table tfoot tr td {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid #f0f0f0;
    text-align: right;
  }

  .summary-table tbody tr td:last-child,
  .summary-table tfoot tr td:last-child {
    border-bottom: none;
  }

  .summary-table tbody tr td::before,
  .summary-table tfoot tr td::before {
    content: attr(data-label);
    font-weight: 600;
    text-align: left;
    color: #333;
  }

  .summary-table tfoot tr {
    background: #f5f5f5;
    border: 2px solid #0969da;
  }

  /* Modal adjustments for mobile */
  .modal-overlay {
    padding: 8px;
  }

  .modal-content {
    max-height: 90vh;
  }

  .modal-header {
    padding: 12px 16px;
  }

  .modal-header h3 {
    font-size: 1rem;
  }

  .modal-body {
    padding: 12px 16px;
  }

  /* File item adjustments for mobile */
  .file-item {
    flex-direction: column;
    padding: 12px;
  }

  .file-icon-wrapper {
    margin-right: 0;
    margin-bottom: 12px;
    align-self: center;
  }

  .file-details p {
    font-size: 0.85rem;
  }

  .btn-view {
    width: 100%;
    justify-content: center;
  }
}

/* Tablet Responsive Styles */
@media (min-width: 769px) and (max-width: 1024px) {
  .month-display h3 {
    font-size: 1.1rem;
  }

  .dropdown-container {
    width: 160px;
  }

  .summary-table {
    font-size: 0.9rem;
  }

  .summary-table thead th,
  .summary-table tbody td {
    padding: 8px 10px;
  }

  .modal-content {
    max-width: 90%;
  }

  .file-icon-wrapper {
    padding: 10px;
  }

  .file-icon {
    font-size: 1.75rem;
  }
}

/* Small mobile devices */
@media (max-width: 480px) {
  .page-identifier {
    font-size: 0.9rem;
  }

  .form-header h1 {
    font-size: 1.25rem;
  }

  .month-display h3 {
    font-size: 0.9rem;
  }

  .dropdown-input {
    font-size: 0.85rem;
    height: 34px;
  }

  .clickable-number {
    padding: 2px 6px;
    font-size: 0.9rem;
  }

  .file-type-badge, .file-status-badge {
    font-size: 0.7rem;
    padding: 3px 8px;
  }

  .btn-view {
    font-size: 0.8rem;
    padding: 6px 12px;
  }
}
</style>